import React, { useState, useEffect } from 'react';
import { ChevronLeft, Plus, Play, Pause, Volume2, Type, Clock, AlertCircle } from 'lucide-react';
import { tts_duration_seconds, extractTTSVariables, validateTTSText, formatTTSTextForDisplay } from '../utils/ttsUtils';

const TTSSidebar = ({ isOpen, onClose, template_content, settemplate_content, currentPageIndex = 0 }) => {
  const [ttsText, setTtsText] = useState('');
  const [speed, setSpeed] = useState(1); // Speed multiplier
  const [processingGroup, setProcessingGroup] = useState(''); // AI queue processing group (blank = default/untagged)
  const [manualDuration, setManualDuration] = useState(''); // User-specified duration override (seconds)
  const [contentType, setContentType] = useState('speech'); // speech, music
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [validationResult, setValidationResult] = useState({ isValid: true, issues: [] });

  // Content type options
  const contentTypeOptions = [
    { value: 'speech', label: 'Speech' },
    { value: 'music', label: 'Music' },
  ];

  // Speed options (applied post-generation to modify the mp3)
  const speedOptions = [
    { value: 0.5, label: '0.5x (Very Slow)' },
    { value: 0.75, label: '0.75x (Slow)' },
    { value: 0.9, label: '0.9x' },
    { value: 0.95, label: '0.95x' },
    { value: 1, label: '1x (Normal)' },
    { value: 1.1, label: '1.1x' },
    { value: 1.25, label: '1.25x' },
    { value: 1.5, label: '1.5x (Fast)' },
    { value: 2, label: '2x (Very Fast)' },
  ];

  // Validate text whenever it changes
  useEffect(() => {
    const result = validateTTSText(ttsText);
    setValidationResult(result);
  }, [ttsText]);

  // Calculate estimated duration based on text and speed
  const baseDuration = tts_duration_seconds(ttsText, 160); // Base duration at normal WPM
  const estimatedDuration = speed > 0 ? baseDuration / speed : baseDuration;
  // Effective duration used for the timeline item: manual override when the user
  // enters a valid positive number, otherwise the estimate.
  const parsedManualDuration = parseFloat(manualDuration);
  const hasManualDuration = !isNaN(parsedManualDuration) && parsedManualDuration > 0;
  const duration = hasManualDuration ? parsedManualDuration : estimatedDuration;
  const variables = extractTTSVariables(ttsText);

  const handleAddTTS = () => {
    if (!validationResult.isValid || !ttsText.trim()) {
      return;
    }

    const currentPage = template_content?.pages?.[currentPageIndex] || {};
    const currentItems = currentPage.items || [];

    const newTTSItem = {
      id: `tts-${Date.now()}`,
      type: 'tts',
      name: `TTS: ${formatTTSTextForDisplay(ttsText, 30)}`,
      // TTS items are audio items in the timeline
      time: {
        start: 0,
        duration: duration
      },
      // TTS-specific properties
      text: ttsText,
      processing_group: processingGroup.trim(),
      speed: speed,
      contentType: contentType,
      duration: duration,
      variables: variables,
      tts_generated_link: '', // Will be filled by the system
      // Audio-specific properties
      settings: {
        volume: 1,
        fadeIn: 0,
        fadeOut: 0,
        pan: 0
      },
      // Mark as non-visual item (audio track)
      isVisual: false,
      layer: -1, // Use negative layer for audio items
      trackId: 0 // Default to track 0
    };

    console.log('🗣️ Adding TTS item to page items:', {
      name: newTTSItem.name,
      text: ttsText,
      duration: duration,
      processing_group: processingGroup.trim(),
      speed: speed,
      contentType: contentType,
      variables: variables,
      trackId: 0
    });

    const updatedTemplate = {
      ...template_content,
      pages: template_content.pages.map((page, idx) =>
        idx === currentPageIndex
          ? { ...page, items: [...currentItems, newTTSItem] }
          : page
      )
    };

    settemplate_content(updatedTemplate);
    
    // Reset form
    setTtsText('');
    setProcessingGroup('');
    setManualDuration('');
    setSpeed(1);
    setContentType('speech');
    
    onClose();
  };

  const handlePreviewTTS = () => {
    if (!validationResult.isValid || !ttsText.trim()) {
      return;
    }

    // This would integrate with a TTS preview service
    console.log('🗣️ Preview TTS:', { text: ttsText, processing_group: processingGroup, speed, contentType });
    setPreviewPlaying(!previewPlaying);
    
    // Simulate preview duration
    if (!previewPlaying) {
      setTimeout(() => {
        setPreviewPlaying(false);
      }, Math.min(duration * 1000, 10000)); // Max 10 seconds preview
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed left-16 top-0 h-full w-96 bg-white border-r border-gray-200 shadow-lg z-500 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <Type className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold">TTS/Music</h2>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">

        {/* Content Type Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Content Type</label>
          <select
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {contentTypeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Text Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Text Content
          </label>
          <textarea
            value={ttsText}
            onChange={(e) => setTtsText(e.target.value)}
            placeholder="Enter text to convert to speech... Use {{variable_name}} for dynamic content."
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            maxLength={5000}
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>{ttsText.length}/5000 characters</span>
            <span>~{duration.toFixed(1)}s duration</span>
          </div>
        </div>

        {/* Validation Messages */}
        {!validationResult.isValid && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <div className="flex items-center space-x-2 text-red-800">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Validation Issues:</span>
            </div>
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
              {validationResult.issues.map((issue, index) => (
                <li key={index}>{issue}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Variables Detected */}
        {variables.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <div className="flex items-center space-x-2 text-blue-800 mb-2">
              <Type className="w-4 h-4" />
              <span className="text-sm font-medium">Variables Detected:</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {variables.map((variable, index) => (
                <span
                  key={index}
                  className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                >
                  {variable}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Generation Settings */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700">Generation Settings</h3>

          {/* Processing Group */}
          <div className="space-y-2">
            <label className="block text-sm text-gray-600">Processing Group</label>
            <input
              type="text"
              value={processingGroup}
              onChange={(e) => setProcessingGroup(e.target.value)}
              placeholder="Leave blank for default"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500">
              Routes this {contentType} job to a specific AI queue group. Leave blank to use the default group.
            </p>
          </div>

          {/* Duration Input */}
          <div className="space-y-2">
            <label className="block text-sm text-gray-600">Duration (seconds)</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={manualDuration}
              onChange={(e) => setManualDuration(e.target.value)}
              placeholder={`Estimated: ${estimatedDuration.toFixed(1)}s`}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500">
              Leave blank to use the estimated duration.
            </p>
          </div>

          {/* Speed Selection */}
          <div className="space-y-2">
            <label className="block text-sm text-gray-600">
              Speed: {speed}x
            </label>
            <select
              value={speed}
              onChange={(e) => setSpeed(parseFloat(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {speedOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Duration Info */}
        <div className="bg-gray-50 rounded-md p-3">
          <div className="flex items-center space-x-2 text-gray-700">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">
              {hasManualDuration ? 'Duration:' : 'Estimated Duration:'}
            </span>
            <span className="text-sm">{duration.toFixed(1)} seconds</span>
          </div>
          {hasManualDuration && (
            <p className="mt-1 text-xs text-gray-500">
              Estimated from text: {estimatedDuration.toFixed(1)}s
            </p>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="border-t border-gray-200 p-4 space-y-3">
        {/* Preview Button */}
        <button
          onClick={handlePreviewTTS}
          disabled={!validationResult.isValid || !ttsText.trim()}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {previewPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          <span>{previewPlaying ? 'Stop Preview' : 'Preview TTS'}</span>
        </button>

        {/* Add Button */}
        <button
          onClick={handleAddTTS}
          disabled={!validationResult.isValid || !ttsText.trim()}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add TTS to Timeline</span>
        </button>
      </div>
    </div>
  );
};

export default TTSSidebar;
